import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User } from '../types';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAdmin: boolean;
  logo: string | null;
  updateLogo: (logoUrl: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [logo, setLogo] = useState<string | null>(() => {
    const savedLogo = localStorage.getItem('hotelLogo');
    return savedLogo || null;
  });

  const createUserProfile = async (userId: string, email: string) => {
    try {
      const { data: existingProfile, error: queryError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (queryError) {
        console.error('Error checking user profile:', queryError);
        return;
      }

      if (!existingProfile) {
        const { error: insertError } = await supabase
          .from('users')
          .insert([{
            id: userId,
            name: email.split('@')[0]
          }]);

        if (insertError) {
          console.error('Error creating user profile:', insertError);
          return;
        }
      }
    } catch (error) {
      console.error('Error in createUserProfile:', error);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          await supabase.auth.signOut();
          setCurrentUser(null);
          return;
        }

        if (session?.user) {
          const isAdmin = (session.user.user_metadata?.is_admin || false) === true;
          
          const { data: profile, error: profileError } = await supabase
            .from('users')
            .select('*')
            .eq('id', session.user.id)
            .maybeSingle();

          if (profileError) {
            console.error('Profile fetch error:', profileError);
            return;
          }

          if (!profile) {
            await createUserProfile(session.user.id, session.user.email || '');
          }

          setCurrentUser({
            id: session.user.id,
            email: session.user.email || '',
            name: profile?.name || session.user.email?.split('@')[0] || '',
            isAdmin: isAdmin
          });
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      }
    };

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          const isAdmin = (session.user.user_metadata?.is_admin || false) === true;

           const { data: profile, error: profileError } = await supabase.auth.getUser(session.access_token);
          if (profileError) {
            console.error('Profile fetch error:', profileError);
            return;
          }

          if (!profile) {
            await createUserProfile(session.user.id, session.user.email || '');
          }

          setCurrentUser({
            id: session.user.id,
            email: session.user.email || '',
            name: profile?.name || session.user.email?.split('@')[0] || '',
            isAdmin: isAdmin
          });
        }
      } else if (event === 'SIGNED_OUT') {
        setCurrentUser(null);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          // Set session expiry to 30 days
          expiresIn: 30 * 24 * 60 * 60
        }
      });

      if (signInError) {
        throw signInError;
      }

      if (!data.user) {
        throw new Error('No user data returned');
      }

      const isAdmin = (data.user.user_metadata?.is_admin || false) === true;
      
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        throw new Error('Failed to fetch user profile');
      }

      if (!profile) {
        await createUserProfile(data.user.id, data.user.email || '');
      }

      setCurrentUser({
        id: data.user.id,
        email: data.user.email || '',
        name: profile?.name || data.user.email?.split('@')[0] || '',
        isAdmin: isAdmin
      });
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setCurrentUser(null);
    } catch (error) {
      console.error('Error during logout:', error);
      throw error;
    }
  };

  const updateLogo = (logoUrl: string) => {
    setLogo(logoUrl);
    localStorage.setItem('hotelLogo', logoUrl);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        login,
        logout,
        isAdmin: currentUser?.isAdmin || false,
        logo,
        updateLogo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};